// Implements: F-IDN-027
/**
 * The organizations a P3 page works on, read from the session's permission answer only
 * (`ScopeEntry.permissions`, UIX.5): never from a role.
 *
 * `organizationTargets` offers each organization once when any of its scopes (the organization
 * itself, or one of its teams for a team lead) carries one of the page's actions, ordered by name in
 * the platform locale. A team lead's session may not name the organization (it holds no seat there
 * with a name of its own), so the name is `null` then and the page says "Organización" instead.
 *
 * `allowsAtOrganization` is the narrower question a control asks: does the ORGANIZATION's own scope
 * carry the action (e.g. "Nuevo equipo" needs `team:create` there, a lead's team scope never does).
 */

import type { BackofficeScope, ScopePermission } from "../../../types/api";
import { PLATFORM_LOCALE } from "../../../types/locale";

export interface OrganizationTarget {
  readonly organizationId: string;
  readonly name: string | null;
}

function organizationOf(scope: BackofficeScope): string | null {
  if (scope.kind === "organization") return scope.id;
  if (scope.kind === "team") return scope.organization_id ?? null;
  return null;
}

function carries(scope: BackofficeScope, actions: readonly ScopePermission[]): boolean {
  return actions.some((action) => scope.permissions?.includes(action) === true);
}

export function organizationTargets(
  scopes: readonly BackofficeScope[],
  actions: readonly ScopePermission[],
): OrganizationTarget[] {
  const ids = new Set<string>();
  for (const scope of scopes) {
    const organizationId = organizationOf(scope);
    if (organizationId !== null && carries(scope, actions)) ids.add(organizationId);
  }
  const nameOf = (id: string): string | null =>
    scopes.find((scope) => scope.kind === "organization" && scope.id === id)?.name ?? null;
  return [...ids]
    .map((organizationId) => ({ organizationId, name: nameOf(organizationId) }))
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", PLATFORM_LOCALE));
}

export function allowsAtOrganization(
  scopes: readonly BackofficeScope[],
  organizationId: string,
  action: ScopePermission,
): boolean {
  return scopes.some(
    (scope) => scope.kind === "organization" && scope.id === organizationId && carries(scope, [action]),
  );
}
